import { DashboardAPI } from '@business/api/dashboard';
import {
  DashboardFilters,
  DashboardId,
  DashboardResponse,
  DashboardWidgetId,
  DashboardWidgetResponse,
  DashboardLink,
  DashboardInterval,
  DashboardSettingResponse,
} from '@business/entities/dashboard';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { ReportBuilderFormData } from '@modules/dynamicReports/ReportForms';
import { forkJoin, BehaviorSubject, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { GraphTypes } from '@business/entities/graph';

export class DashboardBloc extends BaseBloc {
  private readonly dashboardRepo = new Repository<DashboardResponse>({
    getItemId: dashboard => dashboard.id,
  });

  readonly links$ = new BehaviorSubject<DashboardLink[]>([]);
  requestId = new BehaviorSubject<number>(1);

  readonly dashboards$ = this.dashboardRepo.items$.pipe(map(ds => R.sortBy(ds, d => d.title)));
  selectDashboard = this.dashboardRepo.selectItem;

  onReset() {
    this.dashboardRepo.reset();
  }

  fetchDashboards = (isCompact?: boolean) => {
    return DashboardAPI.fetchDashboards(isCompact).pipe(
      this.dashboardRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_dashboards_error') }),
    );
  };

  fetchDashboardTemplates = () => {
    return DashboardAPI.fetchDashboardTemplates().pipe(handleMessage({ error: requestMessage('fetch_dashboards_error') }));
  };

  fetchDashboard = (id: DashboardId, requestId: number, filters?: DashboardFilters) => {
    return DashboardAPI.fetchDashboard(id, requestId, filters).pipe(
      switchMap(dashboard => {
        if (dashboard.requestId === this.requestId.value) {
          return of(dashboard);
        } else {
          return of({} as DashboardResponse);
        }
      }),

      this.dashboardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_dashboards_error') }),
    );
  };

  createDashboard = (data: Partial<DashboardResponse>) => {
    return DashboardAPI.createDashboard(data).pipe(
      this.dashboardRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_dashboard'),
      }),
    );
  };

  updateDashboard = (id: DashboardId, data: Partial<DashboardResponse>) => {
    return DashboardAPI.updateDashboard(id, data).pipe(
      this.dashboardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_dashboard'),
      }),
    );
  };

  makeDefault = (id: DashboardId, interval: DashboardInterval, isDefault: boolean) => {
    return DashboardAPI.makeDefault(id, interval, isDefault).pipe(
      this.dashboardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_dashboard'),
      }),
    );
  };

  duplicateDashboard = (id: DashboardId, data: Partial<DashboardResponse>) => {
    return DashboardAPI.duplicateDashboard(id, data).pipe(
      this.dashboardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_dashboard'),
      }),
    );
  };

  deleteDashboard = (id: DashboardId) => {
    return showDeleteConfirmation('Delete Dashboard', 'Do you really want to remove this dashboard?').pipe(
      switchMap(() => DashboardAPI.deleteDashboard(id)),
      this.dashboardRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_dashboard'),
      }),
    );
  };

  createWidget = (id: DashboardId, data: ReportBuilderFormData) => {
    return DashboardAPI.createWidget(id, data).pipe(
      this.dashboardRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_dashboard'),
      }),
    );
  };

  updateWidget = (widgetId: DashboardWidgetId, data: Partial<ReportBuilderFormData>) => {
    return DashboardAPI.updateWidget(widgetId, data).pipe(this.dashboardRepo.ops.upsertOne(item => ({ item })));
  };

  reloadWidget = (dashboardId: DashboardId, widgetId: DashboardWidgetId, dateRange?: string) => {
    return DashboardAPI.fetchWidget(widgetId, dateRange).pipe(
      this.dashboardRepo.ops.updateOne(item => ({
        id: dashboardId,
        changes: dashboard => {
          const widgets = dashboard.widgets.map(w => (w.id === widgetId ? item : w));
          return { widgets };
        },
      })),
    );
  };

  updateGraphType = (dashboardId: DashboardId, widgetId: DashboardWidgetId, graphType: GraphTypes, dateRange?: string) => {
    return DashboardAPI.updateGraphType(widgetId, graphType, dateRange).pipe(
      this.dashboardRepo.ops.updateOne(item => ({
        id: dashboardId,
        changes: dashboard => {
          const widgets = dashboard.widgets.map(w => (w.id === widgetId ? item : w));
          return { widgets };
        },
      })),
    );
  };

  deleteWidget = (dashboardId: DashboardId, id: DashboardWidgetId) => {
    return showDeleteConfirmation('Delete Widget', 'Do you really want to remove this widget?').pipe(
      switchMap(() => DashboardAPI.deleteWidget(id)),
      this.dashboardRepo.ops.updateOne(() => ({
        id: dashboardId,
        changes: d => ({ widgets: d.widgets.filter(w => w.id !== id) }),
      })),
    );
  };

  updateBulkWidgets = (dashboardId: DashboardId, widgets: DashboardWidgetResponse[]) => {
    this.dashboardRepo.updateOne({ id: dashboardId, changes: () => ({ widgets }) });
    DashboardAPI.updateAllWidgets(dashboardId, widgets).subscribe();
  };

  updateAllWidgets = (dashboardId: DashboardId, widgets: DashboardWidgetResponse[]) => {
    this.dashboardRepo.updateOne({ id: dashboardId, changes: () => ({ widgets }) });
    forkJoin(widgets.map(w => DashboardAPI.updateWidget(w.id, w.settings))).subscribe();
  };

  updateDashboardSettings = (id: DashboardId, data: DashboardSettingResponse) => {
    return DashboardAPI.updateDashboardSettings(id, data);
  };
}
